import { computed, ref, watch, type Ref, type WatchStopHandle } from "vue";

import type { BaseLogger } from "@/components/inject";
import type {
  MeetingRecordingContext,
  RecordingStatus,
  StopRecordingReason,
} from "@/recording/types";

import { useSessionStore } from "../useSessionStore";
import { MEETING_RECORDING_TRANSITION_TO_RECORDING_TIMEOUT_MS } from "./useMeetingRecording";

/**
 * Browser-store key for the auto-recording toggle, namespaced per account
 * (`useBrowserStore(accountId)`) by its consumers (Settings → Meetings, the
 * event surfaces). The key also exists un-namespaced, written by older
 * versions of the app; consumers `migrate` it before reading.
 */
// TODO: rename to preferences/meetings/auto-recording:enabled
export const STORAGE_KEY = "preferences/autoMeetingRecording";

type AutoManagedRecordingState = "started" | "stopped";

type UseMeetingRecordingLike = {
  isSupported: boolean;
  isHostReady: Ref<boolean>;
  status: Ref<RecordingStatus>;
  isEntitledToAIMeetingNotes: Ref<boolean>;
  isRecordingElsewhere: Ref<boolean>;
  isRecordingHere: Ref<boolean>;
  startOrResumeRecording: (options?: {
    anchor?: "event" | "manual";
    force?: boolean;
  }) => Promise<void>;
  refreshState: () => Promise<void>;
  stopRecording: (reason?: StopRecordingReason) => Promise<void>;
  waitForRecordingStatus: (
    desiredStatus: "idle" | "recording" | "paused",
    timeoutMs?: number
  ) => Promise<void>;
};

/*
 * Auto meeting recording architecture:
 *
 * - `useAutoMeetingRecording()` is the top policy layer for recording.
 * - It should not know about IPC, the menubar runtime, or low-level audio
 *   capture. Those concerns live below this layer in `useMeetingRecording()`,
 *   `useAudioRecording()`, `recording/gateway.ts`, and `recording/host.ts`.
 * - Its responsibility is to decide whether the current event should be
 *   recorded automatically, based on the event timing, eligibility, user
 *   preference (`STORAGE_KEY`), entitlement, and per-session auto-managed
 *   state stored in `useSessionStore()`.
 * - When policy says "start", it delegates to `useMeetingRecording()`, which
 *   then performs the meeting-aware orchestration on top of the generic
 *   recording layer. Scheduled stop is owned by the long-lived recording host.
 */
export function useAutoMeetingRecording<TEvent extends MeetingRecordingContext>(
  baseLogger: BaseLogger,
  clock: Ref<Date>,
  meetingRecording: UseMeetingRecordingLike,
  event: Ref<TEvent>,
  isAutoMeetingRecordingEnabled: Ref<boolean>,
  canAutoStart: boolean = false,
  options?: {
    forceStart?: Ref<boolean>;
  }
) {
  const logger = baseLogger.forModule("useAutoMeetingRecording");
  const { syncState } = useSessionStore();
  const autoManagedRecordingStates = syncState<
    Record<string, AutoManagedRecordingState>
  >("meetings/recordings", {}, { deep: true });
  const errorMessage = ref<string | null>(null);
  const isCurrentEvent = computed(() => {
    const t = event.value.target;
    if (t.kind !== "event") return false;
    const { start, end } = t;
    const nowMs = clock.value.getTime();
    return start.getTime() <= nowMs && nowMs < end.getTime();
  });
  const isEntitledToAIMeetingNotes = computed(() => {
    return meetingRecording.isEntitledToAIMeetingNotes.value;
  });
  const isEligibleForAutoRecording = computed(() => {
    const t = event.value.target;
    if (t.kind !== "event") return false;
    return !!t.link && !!t.attendable && !!t.active;
  });
  const isRecordingHere = computed(
    () => meetingRecording.isRecordingHere.value
  );
  const isRecordingElsewhere = computed(
    () => meetingRecording.isRecordingElsewhere.value
  );
  const willAutoRecordEvent = computed(() => {
    const t = event.value.target;
    if (t.kind !== "event") return false;
    const now = clock.value;
    const { start, end } = t;

    return (
      isAutoMeetingRecordingEnabled.value &&
      meetingRecording.isSupported &&
      isEntitledToAIMeetingNotes.value &&
      isEligibleForAutoRecording.value &&
      start <= end &&
      end > now &&
      !isRecordingHere.value &&
      autoManagedRecordingStates.value[event.value.id] !== "stopped"
    );
  });

  const recordingStartsInLabel = computed(() => {
    if (!willAutoRecordEvent.value || isCurrentEvent.value) {
      return null;
    }

    const t = event.value.target;
    if (t.kind !== "event") return null;
    const now = clock.value;
    const { start } = t;
    if (start <= now) return null;

    const sameDay =
      start.getFullYear() === now.getFullYear() &&
      start.getMonth() === now.getMonth() &&
      start.getDate() === now.getDate();
    if (!sameDay) return null;

    const minutesUntilStart = Math.ceil(
      (start.getTime() - now.getTime()) / (1000 * 60)
    );
    return `Will start auto recording in ${minutesUntilStart} minute(s)`;
  });

  const markAutoMeetingRecordinngStarted = (eventId = event.value.id) => {
    autoManagedRecordingStates.value[eventId] = "started";
  };

  const markAutoMeetingRecordinngStopped = (eventId = event.value.id) => {
    autoManagedRecordingStates.value[eventId] = "stopped";
  };

  // Guard against leaked watchers from Suspense unmounting async components.
  // When EventDetails.vue (wrapped in <Suspense>) is destroyed mid-setup,
  // Vue may not properly clean up watchers created after an `await`. The
  // caller must invoke `release()` from an onBeforeUnmount registered
  // *before* the first await in the component's async setup.
  let released = false;
  const watchHandles: WatchStopHandle[] = [];
  // Auto-start can be requested by multiple watchers. Deduplicate the full
  // refresh/readiness/eligibility/start pipeline for the same event.
  let autoStartInFlight: Promise<void> | null = null;
  let autoStartInFlightEventId: string | null = null;
  // Prompt-driven force-start has a separate pipeline and is not event-keyed;
  // deduplicate repeated force signals until that pipeline settles.
  let forceStartInFlight: Promise<void> | null = null;
  // The two pipelines can overlap (for example, a force signal during an
  // auto-start). Track only calls that reached the actual recording start so
  // the toolbar reactively shows "Initializing…" until every attempt settles.
  const startsInFlight = ref(0);
  const isStartingRecording = computed(() => startsInFlight.value > 0);

  const waitForHostReady = (timeoutMs: number) =>
    new Promise<void>((resolve, reject) => {
      if (meetingRecording.isHostReady.value) {
        resolve();
        return;
      }

      let done = false;

      function finish() {
        if (done) return;
        done = true;
        if (timeoutId !== undefined) {
          clearTimeout(timeoutId);
        }
        stopWatch();
      }

      const timeoutId = setTimeout(() => {
        finish();
        reject(new Error("Recording service not ready"));
      }, timeoutMs);

      const stopWatch = watch(meetingRecording.isHostReady, (ready) => {
        if (!ready) return;
        finish();
        resolve();
      });
    });

  const autoStartRecording = async (eventId: string) => {
    if (released) return;
    // Two watchers can fire back-to-back for the same event (see below); only
    // one auto-start should reach the host.
    if (autoStartInFlightEventId === eventId && autoStartInFlight) {
      await autoStartInFlight;
      return;
    }

    const runAutoStart = async () => {
      // Fetch the host's actual state so meetingRecording.status is up-to-date
      // (avoids seeing stale "idle" right after mount).
      await meetingRecording.refreshState();

      if (released) return;

      // The user may have navigated to a different event while we awaited
      // refreshState — bail if this composable's event no longer matches.
      if (event.value.id !== eventId) return;

      try {
        await waitForHostReady(
          MEETING_RECORDING_TRANSITION_TO_RECORDING_TIMEOUT_MS
        );
      } catch {
        return;
      }

      if (released) return;
      if (event.value.id !== eventId) return;

      // If the event is not eligible for auto recording, bail.
      if (!willAutoRecordEvent.value || !isCurrentEvent.value) return;

      await startRecordingForEvent(eventId, "auto");
    };

    autoStartInFlightEventId = eventId;
    autoStartInFlight = runAutoStart();
    try {
      await autoStartInFlight;
    } finally {
      autoStartInFlight = null;
      autoStartInFlightEventId = null;
    }
  };

  const forceStartRecording = async (eventId: string) => {
    if (released) return;
    if (forceStartInFlight) {
      await forceStartInFlight;
      return;
    }

    const runForceStart = async () => {
      await meetingRecording.refreshState();
      if (released) return;
      if (event.value.id !== eventId) return;
      if (!meetingRecording.isSupported) return;
      if (!isEntitledToAIMeetingNotes.value) return;

      await waitForHostReady(
        MEETING_RECORDING_TRANSITION_TO_RECORDING_TIMEOUT_MS
      );
      if (released) return;
      if (event.value.id !== eventId) return;

      await startRecordingForEvent(eventId, "force");
    };

    forceStartInFlight = runForceStart();
    try {
      await forceStartInFlight;
    } finally {
      forceStartInFlight = null;
    }
  };

  const startRecordingForEvent = async (
    eventId: string,
    mode: "auto" | "force"
  ) => {
    if (released) return;

    // User intent is the primary signal: opening or selecting an event means
    // they want to record it. startOrResumeRecording stops any other active
    // session before starting (back-to-back meetings, etc.).
    logger.debug(`${mode}-start: starting recording`, { eventId });
    startsInFlight.value++;
    try {
      errorMessage.value = null;
      await meetingRecording.startOrResumeRecording(
        mode === "force"
          ? { anchor: "manual", force: true }
          : { anchor: "event" }
      );
      markAutoMeetingRecordinngStarted(eventId);
      logger.debug(`${mode}-start: recording started`, { eventId });
    } catch (error) {
      errorMessage.value =
        error instanceof Error ? error.message : String(error);
      logger.error(`${mode}-start: failed to start recording`, { error });
    } finally {
      startsInFlight.value--;
    }
  };

  if (canAutoStart) {
    logger.debug("auto-start: mounted", { eventId: event.value.id });

    // Trigger on mount (immediate) and when the component switches to a
    // different event without remounting (e.g. clicking another event in the
    // sidebar). Guards inside autoStartRecording (willAutoRecordEvent,
    // isCurrentEvent, etc.) ensure only eligible events are recorded.
    watchHandles.push(
      watch(
        () => event.value.id,
        (eventId) => {
          void autoStartRecording(eventId);
        },
        { immediate: true }
      )
    );

    // The user opened the event before it started — start recording once
    // the event becomes current by time.
    //
    // Use `previousCurrent === false`, not `!previousCurrent`: on mount the
    // event-id watcher above already auto-starts current events; treating
    // `undefined` as "was not current" would double-start.
    watchHandles.push(
      watch(isCurrentEvent, (current, previousCurrent) => {
        if (current && previousCurrent === false) {
          void autoStartRecording(event.value.id);
        }
      })
    );
  }

  const forceStart = options?.forceStart;
  if (forceStart) {
    watchHandles.push(
      watch(
        forceStart,
        (shouldForceStart) => {
          if (!shouldForceStart) return;
          void forceStartRecording(event.value.id);
        },
        { immediate: true }
      )
    );
  }

  const release = () => {
    if (released) return;
    released = true;
    for (const stop of watchHandles) stop();
    watchHandles.length = 0;
    autoManagedRecordingStates.release();
    if (canAutoStart) {
      logger.debug("auto-start: released", { eventId: event.value.id });
    }
  };

  return {
    release,
    errorMessage,
    isCurrentEvent,
    isEntitledToAIMeetingNotes,
    isEligibleForAutoRecording,
    isRecordingElsewhere,
    isRecordingHere,
    isStartingRecording,
    willAutoRecordEvent,
    recordingStartsInLabel,
    markAutoMeetingRecordinngStarted,
    markAutoMeetingRecordinngStopped,
  };
}
